import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface Bot {
  id: string;
  name: string;
  type: string;
  status: string;
}

interface ConditionalSkipSetting {
  id: string;
  bot_id: string;
  enabled: boolean;
  forward_enabled: boolean;
  skip_type: string;
  forward_url: string | null;
  bots?: Bot;
}

interface NormalPattern {
  id: string;
  setting_id: string;
  lock_pattern: string;
  open_pattern: string;
  priority: number;
  enabled: boolean;
}

interface PermanentPattern {
  id: string;
  setting_id: string;
  lock_pattern: string;
  priority: number;
  enabled: boolean;
}

interface Type2PatternsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setting: ConditionalSkipSetting;
  onUpdate: (id: string, updates: any) => void;
  isUpdating: boolean;
}

export const Type2PatternsDialog: React.FC<Type2PatternsDialogProps> = ({
  open,
  onOpenChange,
  setting,
  onUpdate,
  isUpdating,
}) => {
  const queryClient = useQueryClient();
  const [forwardUrl, setForwardUrl] = useState(setting.forward_url || '');
  const [normalPatterns, setNormalPatterns] = useState<NormalPattern[]>([]);
  const [permanentPatterns, setPermanentPatterns] = useState<PermanentPattern[]>([]);

  // Fetch patterns
  const { data: fetchedPatterns = [], isLoading } = useQuery({
    queryKey: ['flexible-patterns', setting.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('flexible_skip_patterns')
        .select('*')
        .eq('setting_id', setting.id)
        .order('priority');
      
      if (error) throw error;
      
      // Add is_permanent default for existing patterns
      return (data || []).map((p: any) => ({
        ...p,
        is_permanent: p.is_permanent ?? false
      }));
    },
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setForwardUrl(setting.forward_url || '');
      
      // Split fetched patterns into normal and permanent
      const normal = fetchedPatterns
        .filter(p => !p.is_permanent)
        .map(p => ({
          id: p.id,
          setting_id: p.setting_id,
          lock_pattern: p.lock_pattern,
          open_pattern: p.open_pattern,
          priority: p.priority,
          enabled: p.enabled,
        }));
      
      const permanent = fetchedPatterns
        .filter(p => p.is_permanent)
        .map(p => ({
          id: p.id,
          setting_id: p.setting_id,
          lock_pattern: p.lock_pattern,
          priority: p.priority,
          enabled: p.enabled,
        }));
      
      setNormalPatterns(normal);
      setPermanentPatterns(permanent);
    }
  }, [open, setting, fetchedPatterns]);

  // Save patterns mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      // Update forward URL
      if (forwardUrl !== setting.forward_url) {
        const { error: urlError } = await supabase
          .from('conditional_skip_settings')
          .update({ forward_url: forwardUrl })
          .eq('id', setting.id);
        
        if (urlError) throw urlError;
      }

      // Merge patterns with is_permanent flag
      const allPatterns = [
        ...normalPatterns.map(p => ({ ...p, is_permanent: false })),
        ...permanentPatterns.map(p => ({ ...p, is_permanent: true, open_pattern: '' }))
      ];

      // Delete removed patterns
      const existingIds = fetchedPatterns.map(p => p.id);
      const newIds = allPatterns.filter(p => p.id).map(p => p.id);
      const deletedIds = existingIds.filter(id => !newIds.includes(id));
      
      if (deletedIds.length > 0) {
        const { error: deleteError } = await supabase
          .from('flexible_skip_patterns')
          .delete()
          .in('id', deletedIds);
        
        if (deleteError) throw deleteError;
      }

      // Update or insert patterns
      for (const pattern of allPatterns) {
        if (pattern.id) {
          // Update existing
          const { error: updateError } = await supabase
            .from('flexible_skip_patterns')
            .update({
              lock_pattern: pattern.lock_pattern,
              open_pattern: pattern.open_pattern,
              priority: pattern.priority,
              enabled: pattern.enabled,
              is_permanent: pattern.is_permanent,
            })
            .eq('id', pattern.id);
          
          if (updateError) throw updateError;
        } else {
          // Insert new
          const { error: insertError } = await supabase
            .from('flexible_skip_patterns')
            .insert({
              setting_id: setting.id,
              lock_pattern: pattern.lock_pattern,
              open_pattern: pattern.open_pattern,
              priority: pattern.priority,
              enabled: true,
              is_permanent: pattern.is_permanent,
            });
          
          if (insertError) throw insertError;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flexible-patterns', setting.id] });
      queryClient.invalidateQueries({ queryKey: ['conditional-skip-settings'] });
      toast.success('Patterns saved successfully');
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error(`Failed to save patterns: ${error.message}`);
    },
  });

  // Normal pattern handlers
  const handleAddNormalPattern = () => {
    const existingPriorities = [...normalPatterns.map(p => p.priority), ...permanentPatterns.map(p => p.priority)];
    const newPriority = existingPriorities.length > 0 
      ? Math.max(...existingPriorities) + 1 
      : 1;
    
    setNormalPatterns([
      ...normalPatterns,
      {
        id: '',
        setting_id: setting.id,
        lock_pattern: '',
        open_pattern: '',
        priority: newPriority,
        enabled: true,
      },
    ]);
  };

  const handleRemoveNormalPattern = (index: number) => {
    setNormalPatterns(normalPatterns.filter((_, i) => i !== index));
  };

  const handleUpdateNormalPattern = (index: number, field: keyof NormalPattern, value: any) => {
    const updated = [...normalPatterns];
    updated[index] = { ...updated[index], [field]: value };
    setNormalPatterns(updated);
  };

  // Permanent pattern handlers
  const handleAddPermanentPattern = () => {
    const existingPriorities = [...normalPatterns.map(p => p.priority), ...permanentPatterns.map(p => p.priority)];
    const newPriority = existingPriorities.length > 0 
      ? Math.max(...existingPriorities) + 1 
      : 1;
    
    setPermanentPatterns([
      ...permanentPatterns,
      {
        id: '',
        setting_id: setting.id,
        lock_pattern: '',
        priority: newPriority,
        enabled: true,
      },
    ]);
  };

  const handleRemovePermanentPattern = (index: number) => {
    setPermanentPatterns(permanentPatterns.filter((_, i) => i !== index));
  };

  const handleUpdatePermanentPattern = (index: number, field: keyof PermanentPattern, value: any) => {
    const updated = [...permanentPatterns];
    updated[index] = { ...updated[index], [field]: value };
    setPermanentPatterns(updated);
  };

  // Validation functions
  const validateNormalPatterns = () => {
    for (const p of normalPatterns) {
      if (!p.lock_pattern || !p.lock_pattern.trim()) {
        toast.error('Normal patterns: Lock pattern is required');
        return false;
      }
      
      if (!/^[01]+$/.test(p.lock_pattern)) {
        toast.error('Normal patterns: Lock pattern must only contain 0 and 1');
        return false;
      }
      
      if (!p.open_pattern || !p.open_pattern.trim()) {
        toast.error('Normal patterns: Open pattern is required');
        return false;
      }
      
      if (!/^[01]+$/.test(p.open_pattern)) {
        toast.error('Normal patterns: Open pattern must only contain 0 and 1');
        return false;
      }
    }
    return true;
  };

  const validatePermanentPatterns = () => {
    for (const p of permanentPatterns) {
      if (!p.lock_pattern || !p.lock_pattern.trim()) {
        toast.error('Permanent locks: Lock pattern is required');
        return false;
      }
      
      if (!/^[01]+$/.test(p.lock_pattern)) {
        toast.error('Permanent locks: Lock pattern must only contain 0 and 1');
        return false;
      }
    }
    return true;
  };

  const handleSave = () => {
    // Check if at least one pattern exists
    if (normalPatterns.length === 0 && permanentPatterns.length === 0) {
      toast.error('Please add at least one pattern rule');
      return;
    }

    // Validate both sections
    if (!validateNormalPatterns() || !validatePermanentPatterns()) {
      return;
    }

    saveMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pattern Rules: {setting.bots?.name}</DialogTitle>
          <DialogDescription>
            Configure flexible pattern matching rules for Type 2 skip logic
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Forward URL */}
          <div>
            <Label htmlFor="forwardUrl">Forward URL</Label>
            <Input
              id="forwardUrl"
              type="url"
              value={forwardUrl}
              onChange={(e) => setForwardUrl(e.target.value)}
              placeholder="https://example.com/webhook"
            />
          </div>

          {/* SECTION 1: Normal Lock/Open Patterns */}
          <div className="space-y-3 p-4 border rounded-lg bg-card">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-lg font-semibold">Lock/Open Patterns</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Patterns that automatically unlock when open pattern is matched
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={handleAddNormalPattern}>
                <Plus className="h-4 w-4 mr-1" />
                Add Pattern
              </Button>
            </div>

            {isLoading ? (
              <div className="flex justify-center p-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : normalPatterns.length === 0 ? (
              <div className="text-center p-6 text-muted-foreground border-2 border-dashed rounded-lg">
                No lock/open patterns yet. Click "Add Pattern" to create one.
              </div>
            ) : (
              <div className="space-y-3">
                {normalPatterns.map((pattern, idx) => (
                  <Card key={idx} className="p-4">
                    <div className="grid grid-cols-[1fr,1fr,auto,auto] gap-3 items-center">
                      <div>
                        <Label className="text-xs mb-1">Lock Pattern</Label>
                        <Input
                          placeholder="e.g., 0000"
                          value={pattern.lock_pattern}
                          onChange={(e) => handleUpdateNormalPattern(idx, 'lock_pattern', e.target.value)}
                          className="font-mono"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs mb-1">Open Pattern</Label>
                        <Input
                          placeholder="e.g., 11"
                          value={pattern.open_pattern}
                          onChange={(e) => handleUpdateNormalPattern(idx, 'open_pattern', e.target.value)}
                          className="font-mono"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs mb-1">Priority</Label>
                        <Input
                          type="number"
                          min="1"
                          value={pattern.priority}
                          onChange={(e) => handleUpdateNormalPattern(idx, 'priority', parseInt(e.target.value) || 1)}
                          className="w-20"
                        />
                      </div>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveNormalPattern(idx)}
                        className="mt-5"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* SECTION 2: Permanent Locks */}
          <div className="space-y-3 p-4 border-2 border-destructive/20 rounded-lg bg-destructive/5">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-lg font-semibold text-destructive flex items-center gap-2">
                  Permanent Locks
                  <span className="text-xl">⚠️</span>
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Once matched, gate stays locked until admin manually unlocks
                </p>
              </div>
              <Button size="sm" variant="destructive" onClick={handleAddPermanentPattern}>
                <Plus className="h-4 w-4 mr-1" />
                Add Permanent Lock
              </Button>
            </div>

            {isLoading ? (
              <div className="flex justify-center p-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : permanentPatterns.length === 0 ? (
              <div className="text-center p-6 text-muted-foreground border-2 border-dashed border-destructive/30 rounded-lg">
                No permanent locks yet. Click "Add Permanent Lock" to create one.
              </div>
            ) : (
              <div className="space-y-3">
                {permanentPatterns.map((pattern, idx) => (
                  <Card key={idx} className="p-4 border-destructive">
                    <div className="grid grid-cols-[1fr,auto,auto] gap-3 items-center">
                      <div>
                        <Label className="text-xs mb-1">Lock Pattern</Label>
                        <Input
                          placeholder="e.g., 0000"
                          value={pattern.lock_pattern}
                          onChange={(e) => handleUpdatePermanentPattern(idx, 'lock_pattern', e.target.value)}
                          className="font-mono"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs mb-1">Priority</Label>
                        <Input
                          type="number"
                          min="1"
                          value={pattern.priority}
                          onChange={(e) => handleUpdatePermanentPattern(idx, 'priority', parseInt(e.target.value) || 1)}
                          className="w-20"
                        />
                      </div>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemovePermanentPattern(idx)}
                        className="mt-5"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)} 
            disabled={saveMutation.isPending}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saveMutation.isPending || (normalPatterns.length === 0 && permanentPatterns.length === 0)}
          >
            {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
